import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'es' | 'sv';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation dictionary
const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navigation
    'nav.about': 'About',
    'nav.services': 'Services',
    'nav.pricing': 'Pricing',
    'nav.faq': 'FAQ',
    'nav.blog': 'Blog',
    'nav.contact': 'Contact',
    'nav.book': 'Book Consultation',
    
    // Home
    'home.hero.title': 'Expert Dermatology Care. Personalized Solutions.',
    'home.hero.subtitle': "Comprehensive dermatology services for skin cancer screening, acne treatment, laser therapy, and all your skin concerns. I combine expertise from Barcelona's leading medical institution with years of international experience in evidence-based dermatology.",
    'home.hero.cta1': 'Schedule Consultation',
    'home.hero.cta2': 'Learn About Services',
    'home.about.title': 'Meet Dr. Raquel Molina',
    'home.about.bio1': "I'm a dermatologist educated at the Universitat Autònoma de Barcelona (UAB), one of Spain's most prestigious medical institutions. My passion for dermatology stems from understanding that skin health profoundly impacts overall wellbeing and confidence.",
    'home.about.bio2': 'After completing my specialization, I spent formative years working in Sweden, where I learned evidence-based medicine and patient-centered care approaches that continue to guide my practice today. I remain committed to staying at the forefront of dermatological research through international conferences and continuous learning.',
    'home.about.bio3': "I specialize in comprehensive dermatology—from skin cancer screening and acne treatment to laser therapy and hair loss solutions. I've seen firsthand how proper dermatological care restores not just skin health, but confidence and quality of life.",
    'home.about.bio_link': 'Read Full Biography',
    'home.services.title': 'Comprehensive Dermatology Services',
    'home.services.subtitle': 'Evidence-based treatments tailored to your specific needs',
    'home.faq.title': 'Frequently Asked Questions',
    'home.faq.subtitle': 'Get answers to common questions about skin cancer, acne, laser treatments, and all dermatology concerns',
    'home.faq.view': 'View All FAQs',
    'home.cta.title': 'Ready to Take the First Step?',
    'home.cta.subtitle': 'Schedule a consultation to discuss your dermatology concerns and explore personalized treatment options.',
    'home.cta.button': 'Book Your Consultation',
    'home.services.skin_cancer': 'Skin Cancer Screening',
    'home.services.skin_cancer_desc': 'Professional mole evaluation, skin cancer detection, and prevention strategies. Early detection saves lives.',
    'home.services.acne': 'Acne Treatment',
    'home.services.acne_desc': 'Personalized acne solutions from mild to severe. Advanced treatments tailored to your skin type and needs.',
    'home.services.laser': 'Laser & Advanced Therapies',
    'home.services.laser_desc': 'Cutting-edge laser treatments for hair loss, skin rejuvenation, and various dermatological conditions.',
    'home.services.view_all': 'View All Services',
    'home.why_choose.title': 'Why Choose Dr. Raquel Molina?',
    'home.why_choose.subtitle': 'International expertise combined with personalized, evidence-based care',
    'home.why_choose.education': 'Barcelona Education',
    'home.why_choose.education_desc': "Trained at Spain's leading medical institution",
    'home.why_choose.international': 'International Experience',
    'home.why_choose.international_desc': 'Years of practice in Sweden and beyond',
    'home.why_choose.evidence': 'Evidence-Based',
    'home.why_choose.evidence_desc': 'Latest dermatological research and techniques',
    'home.why_choose.response': 'Fast Response',
    'home.why_choose.response_desc': '2-hour response, 30-min for emergencies',
    'home.urgent.title': 'Need Help Urgently?',
    'home.urgent.subtitle': "Tell us about your skin concern and we'll respond within 2 hours. For emergencies, we guarantee a response within 30 minutes.",
    'home.urgent.button': 'Submit Your Concern Now',
    'home.urgent.guarantee': 'Our Response Guarantee:',
    'home.urgent.hours2': '2 hours:',
    'home.urgent.hours2_desc': 'Standard response time',
    'home.urgent.hours30': '30 minutes:',
    'home.urgent.hours30_desc': 'Emergency cases',
    'home.urgent.hours24': '24-48 hours:',
    'home.urgent.hours24_desc': 'Appointment availability',
    
    // Services
    'services.title': 'Services',
    'services.subtitle': 'Comprehensive dermatology services specializing in hair loss treatment',
    'services.hair_loss.title': 'Hair Loss Treatment',
    'services.hair_loss.desc': 'Comprehensive evaluation and personalized treatment plans for all types of hair loss, including androgenetic alopecia, telogen effluvium, and alopecia areata.',
    'services.laser.title': 'Low-Level Laser Therapy',
    'services.laser.desc': 'Advanced laser technology to stimulate hair follicles and promote regrowth. FDA-approved with minimal side effects.',
    'services.general.title': 'General Dermatology',
    'services.general.desc': 'Complete dermatological care including mole removal, skin cancer screening, acne treatment, and general skin health.',
    'services.aquapure.title': 'AquaPure Hydrating Facial',
    'services.aquapure.desc': 'Advanced aqueous facial treatment combining deep cleansing, electroporation, and collagen stimulation for radiant, rejuvenated skin.',
    'services.consultation.title': 'Your First Consultation',
    'services.consultation.step1': 'Initial Assessment',
    'services.consultation.step1_desc': 'Comprehensive evaluation of your condition, medical history, and treatment goals.',
    'services.consultation.step2': 'Diagnosis',
    'services.consultation.step2_desc': 'Professional diagnosis using clinical examination and advanced diagnostic tools when needed.',
    'services.consultation.step3': 'Treatment Plan',
    'services.consultation.step3_desc': 'Personalized treatment plan tailored to your specific condition and goals.',
    'services.consultation.step4': 'Follow-up',
    'services.consultation.step4_desc': 'Regular monitoring and adjustments to ensure optimal results.',
    
    // FAQ
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Answers to common questions about hair loss and dermatology treatment from Dr. Raquel Molina',
    
    // Blog
    'blog.title': 'Blog',
    'blog.subtitle': 'Insights and articles from Dr. Raquel Molina on hair loss treatment, dermatology, and skincare. Based on clinical experience and the latest evidence-based research.',
    'blog.search': 'Search articles...',
    'blog.showing': 'Showing',
    'blog.of': 'of',
    'blog.articles': 'articles',
    'blog.no_results': 'No articles found matching your search.',
    'blog.questions': 'Have Questions?',
    'blog.questions_subtitle': "Check out my FAQ section or schedule a consultation to discuss your specific dermatology concerns.",
    'blog.view_faq': 'View FAQ',
    'blog.book': 'Book Consultation',
    'blog.related': 'Related Articles',
    'blog.read_more': 'Read More',
    
    // Contact
    'contact.title': 'Contact & Booking',
    'contact.subtitle': 'Get in touch to schedule your dermatology consultation',
    'contact.form.name': 'Full Name',
    'contact.form.email': 'Email Address',
    'contact.form.phone': 'Phone Number',
    'contact.form.concern': 'Main Concern',
    'contact.form.message': 'Message',
    'contact.form.submit': 'Send Message',
    'contact.info.title': 'Contact Information',
    'contact.info.email': 'skinbyraq@gmail.com',
    'contact.info.phone': '+34 627 18 32 20',
    'contact.info.location': 'Barcelona, Spain',
    
    // About
    'about.title': 'About Dr. Raquel Molina',
    'about.bio': 'Dr. Raquel Molina is a board-certified dermatologist with extensive experience in hair loss treatment and general dermatology. Educated at the Universitat Autònoma de Barcelona, one of Spain\'s most prestigious medical institutions, Dr. Molina combines rigorous scientific training with compassionate patient care.',
    'about.credentials': 'Credentials & Experience',
    'about.education': 'Medical Education',
    'about.education_detail': 'Universitat Autònoma de Barcelona (UAB) - Medical Degree and Dermatology Specialization',
    'about.international': 'International Experience',
    'about.international_detail': 'Multiple years working in Sweden, gaining expertise in evidence-based dermatology and patient-centered care approaches',
    'about.conferences': 'Professional Development',
    'about.conferences_detail': 'Regular attendance and presentations at international dermatology conferences',
    'about.philosophy': 'Treatment Philosophy',
    'about.philosophy_detail': 'Dr. Molina believes in comprehensive, personalized care. She takes time to understand each patient\'s unique situation and develops tailored treatment plans combining the latest evidence-based therapies with proven traditional approaches.',
    
    // Footer
    'footer.about': 'Professional dermatology services specializing in hair loss treatment and comprehensive skin care.',
    'footer.services': 'Services',
    'footer.hair_loss': 'Hair Loss Treatment',
    'footer.laser': 'Laser Therapy',
    'footer.general': 'General Dermatology',
    'footer.resources': 'Resources',
    'footer.faq': 'FAQ',
    'footer.blog': 'Blog',
    'footer.about_dr': 'About Dr. Raquel Molina',
    'footer.contact': 'Contact',
    'footer.copyright': '© 2026 Skin by Raquel. All rights reserved.',
    
    // Testimonials
    'testimonials.title': 'Patient Testimonials',
    'testimonials.subtitle': 'Real stories from patients who have experienced Dr. Raquel Molina\'s care',
    
    // Booking
    'booking.title': 'Book Your Consultation',
    'booking.subtitle': 'Schedule your dermatology appointment with Dr. Raquel Molina',
    
    // Videos
    'videos.title': 'Educational Videos',
    'videos.subtitle': 'Learn about dermatology treatments and skincare from Dr. Raquel Molina',
  },
  es: {
    // Navigation
    'nav.about': 'Acerca de',
    'nav.services': 'Servicios',
    'nav.pricing': 'Tarifas',
    'nav.faq': 'Preguntas',
    'nav.blog': 'Blog',
    'nav.contact': 'Contacto',
    'nav.book': 'Reservar Consulta',
    
    // Home
    'home.hero.title': 'Cuidado Dermatológico Experto. Soluciones Personalizadas.',
    'home.hero.subtitle': 'Servicios integrales de dermatología para cribado de cáncer de piel, tratamiento del acné, terapia láser y todas tus preocupaciones de piel. Combino la experiencia de la institución médica líder de Barcelona con años de experiencia internacional en dermatología basada en evidencia.',
    'home.hero.cta1': 'Reservar Consulta',
    'home.hero.cta2': 'Conocer Servicios',
    'home.about.title': 'Conoce a la Dra. Raquel Molina',
    'home.about.bio1': 'Soy dermatóloga formada en la Universitat Autònoma de Barcelona (UAB), una de las instituciones médicas más prestigiosas de España. Mi pasión por la dermatología surge de la comprensión de que la salud de la piel impacta profundamente en el bienestar general y la confianza.',
    'home.about.bio2': 'Después de completar mi especialización, pasé años formativos trabajando en Suecia, donde aprendí medicina basada en evidencia y enfoques de atención centrados en el paciente que continúan guiando mi práctica hoy. Sigo comprometida a estar a la vanguardia de la investigación dermatológica a través de conferencias internacionales y aprendizaje continuo.',
    'home.about.bio3': 'Me especializo en dermatología integral: desde cribado de cáncer de piel y tratamiento del acné hasta terapia láser y soluciones para la caída del cabello. He visto de primera mano cómo el cuidado dermatológico adecuado restaura no solo la salud de la piel, sino la confianza y la calidad de vida.',
    'home.about.bio_link': 'Leer Biografía Completa',
    'home.services.title': 'Servicios Integrales de Dermatología',
    'home.services.subtitle': 'Tratamientos basados en evidencia adaptados a tus necesidades específicas',
    'home.faq.title': 'Preguntas Frecuentes',
    'home.faq.subtitle': 'Obtén respuestas a preguntas comunes sobre cáncer de piel, acné, tratamientos láser y todas las preocupaciones dermatológicas',
    'home.faq.view': 'Ver Todas las Preguntas',
    'home.cta.title': '¿Listo para Dar el Primer Paso?',
    'home.cta.subtitle': 'Reserva una consulta para discutir tus preocupaciones dermatológicas y explorar opciones de tratamiento personalizadas.',
    'home.cta.button': 'Reservar Tu Consulta',
    'home.services.skin_cancer': 'Cribado de Cáncer de Piel',
    'home.services.skin_cancer_desc': 'Evaluación profesional de lunares, detección de cáncer de piel y estrategias de prevención. La detección temprana salva vidas.',
    'home.services.acne': 'Tratamiento del Acné',
    'home.services.acne_desc': 'Soluciones personalizadas para el acné de leve a grave. Tratamientos avanzados adaptados a tu tipo de piel y necesidades.',
    'home.services.laser': 'Terapia Láser y Tratamientos Avanzados',
    'home.services.laser_desc': 'Tratamientos láser de última generación para la caída del cabello, rejuvenecimiento de la piel y diversas condiciones dermatológicas.',
    'home.services.view_all': 'Ver Todos los Servicios',
    'home.why_choose.title': '¿Por Qué Elegir a la Dra. Raquel Molina?',
    'home.why_choose.subtitle': 'Experiencia internacional combinada con atención personalizada basada en evidencia',
    'home.why_choose.education': 'Formación en Barcelona',
    'home.why_choose.education_desc': 'Formada en la institución médica líder de España',
    'home.why_choose.international': 'Experiencia Internacional',
    'home.why_choose.international_desc': 'Años de práctica en Suecia y más allá',
    'home.why_choose.evidence': 'Basada en Evidencia',
    'home.why_choose.evidence_desc': 'Últimas técnicas e investigación dermatológica',
    'home.why_choose.response': 'Respuesta Rápida',
    'home.why_choose.response_desc': 'Respuesta en 2 horas, 30 min para emergencias',
    'home.urgent.title': '¿Necesitas Ayuda Urgente?',
    'home.urgent.subtitle': 'Cuéntanos sobre tu preocupación de piel y responderemos en 2 horas. Para emergencias, garantizamos una respuesta en 30 minutos.',
    'home.urgent.button': 'Envía Tu Preocupación Ahora',
    'home.urgent.guarantee': 'Nuestra Garantía de Respuesta:',
    'home.urgent.hours2': '2 horas:',
    'home.urgent.hours2_desc': 'Tiempo de respuesta estándar',
    'home.urgent.hours30': '30 minutos:',
    'home.urgent.hours30_desc': 'Casos de emergencia',
    'home.urgent.hours24': '24-48 horas:',
    'home.urgent.hours24_desc': 'Disponibilidad de citas',
    
    // Services
    'services.title': 'Servicios',
    'services.subtitle': 'Servicios integrales de dermatología especializados en tratamiento de la caída del cabello',
    'services.hair_loss.title': 'Tratamiento de la Caída del Cabello',
    'services.hair_loss.desc': 'Evaluación integral y planes de tratamiento personalizados para todos los tipos de caída del cabello, incluyendo alopecia androgénica, efluvio telógeno y alopecia areata.',
    'services.laser.title': 'Terapia Láser de Bajo Nivel',
    'services.laser.desc': 'Tecnología láser avanzada para estimular los folículos capilares y promover el recrecimiento. Aprobada por la FDA con efectos secundarios mínimos.',
    'services.general.title': 'Dermatología General',
    'services.general.desc': 'Atención dermatológica completa incluyendo extirpación de lunares, cribado de cáncer de piel, tratamiento del acné y salud general de la piel.',
    'services.aquapure.title': 'Facial Hidratante AquaPure',
    'services.aquapure.desc': 'Tratamiento facial acuoso avanzado que combina limpieza profunda, electroporación y estimulación de colágeno para una piel radiante y rejuvenecida.',
    'services.consultation.title': 'Tu Primera Consulta',
    'services.consultation.step1': 'Evaluación Inicial',
    'services.consultation.step1_desc': 'Evaluación integral de tu condición, historial médico y objetivos de tratamiento.',
    'services.consultation.step2': 'Diagnóstico',
    'services.consultation.step2_desc': 'Diagnóstico profesional usando examen clínico y herramientas de diagnóstico avanzadas cuando sea necesario.',
    'services.consultation.step3': 'Plan de Tratamiento',
    'services.consultation.step3_desc': 'Plan de tratamiento personalizado adaptado a tu condición específica y objetivos.',
    'services.consultation.step4': 'Seguimiento',
    'services.consultation.step4_desc': 'Monitoreo regular y ajustes para asegurar resultados óptimos.',
    
    // FAQ
    'faq.title': 'Preguntas Frecuentes',
    'faq.subtitle': 'Respuestas a preguntas comunes sobre tratamiento de la caída del cabello y dermatología de la Dra. Raquel Molina',
    
    // Blog
    'blog.title': 'Blog',
    'blog.subtitle': 'Perspectivas y artículos de la Dra. Raquel Molina sobre tratamiento de la caída del cabello, dermatología y cuidado de la piel. Basado en experiencia clínica e investigación basada en evidencia.',
    'blog.search': 'Buscar artículos...',
    'blog.showing': 'Mostrando',
    'blog.of': 'de',
    'blog.articles': 'artículos',
    'blog.no_results': 'No se encontraron artículos que coincidan con tu búsqueda.',
    'blog.questions': '¿Tienes Preguntas?',
    'blog.questions_subtitle': 'Consulta mi sección de preguntas frecuentes o reserva una consulta para discutir tus preocupaciones dermatológicas específicas.',
    'blog.view_faq': 'Ver Preguntas Frecuentes',
    'blog.book': 'Reservar Consulta',
    'blog.related': 'Artículos Relacionados',
    'blog.read_more': 'Leer Más',
    
    // Contact
    'contact.title': 'Contacto y Reservas',
    'contact.subtitle': 'Ponte en contacto para reservar tu consulta de dermatología',
    'contact.form.name': 'Nombre Completo',
    'contact.form.email': 'Correo Electrónico',
    'contact.form.phone': 'Número de Teléfono',
    'contact.form.concern': 'Preocupación Principal',
    'contact.form.message': 'Mensaje',
    'contact.form.submit': 'Enviar Mensaje',
    'contact.info.title': 'Información de Contacto',
    'contact.info.email': 'skinbyraq@gmail.com',
    'contact.info.phone': '+34 627 18 32 20',
    'contact.info.location': 'Barcelona, España',
    
    // About
    'about.title': 'Acerca de la Dra. Raquel Molina',
    'about.bio': 'La Dra. Raquel Molina es una dermatóloga certificada con amplia experiencia en tratamiento de la caída del cabello y dermatología general. Formada en la Universitat Autònoma de Barcelona, una de las instituciones médicas más prestigiosas de España, la Dra. Molina combina una rigurosa formación científica con atención compasiva al paciente.',
    'about.credentials': 'Credenciales y Experiencia',
    'about.education': 'Formación Médica',
    'about.education_detail': 'Universitat Autònoma de Barcelona (UAB) - Licenciatura en Medicina y Especialización en Dermatología',
    'about.international': 'Experiencia Internacional',
    'about.international_detail': 'Múltiples años trabajando en Suecia, adquiriendo experiencia en dermatología basada en evidencia y enfoques de atención centrados en el paciente',
    'about.conferences': 'Desarrollo Profesional',
    'about.conferences_detail': 'Asistencia regular y presentaciones en conferencias internacionales de dermatología',
    'about.philosophy': 'Filosofía de Tratamiento',
    'about.philosophy_detail': 'La Dra. Molina cree en la atención integral y personalizada. Se toma tiempo para entender la situación única de cada paciente y desarrolla planes de tratamiento personalizados combinando las terapias más recientes basadas en evidencia con enfoques tradicionales comprobados.',
    
    // Footer
    'footer.about': 'Servicios profesionales de dermatología especializados en tratamiento de la caída del cabello y cuidado integral de la piel.',
    'footer.services': 'Servicios',
    'footer.hair_loss': 'Tratamiento de Caída del Cabello',
    'footer.laser': 'Terapia Láser',
    'footer.general': 'Dermatología General',
    'footer.resources': 'Recursos',
    'footer.faq': 'Preguntas',
    'footer.blog': 'Blog',
    'footer.about_dr': 'Acerca de la Dra. Raquel Molina',
    'footer.contact': 'Contacto',
    'footer.copyright': '© 2026 Skin by Raquel. Todos los derechos reservados.',
    
    // Testimonials
    'testimonials.title': 'Testimonios de Pacientes',
    'testimonials.subtitle': 'Historias reales de pacientes que han experimentado la atención de la Dra. Raquel Molina',
    
    // Booking
    'booking.title': 'Reserva Tu Consulta',
    'booking.subtitle': 'Programa tu cita de dermatología con la Dra. Raquel Molina',
    
    // Videos
    'videos.title': 'Videos Educativos',
    'videos.subtitle': 'Aprende sobre tratamientos dermatológicos y cuidado de la piel de la Dra. Raquel Molina',
  },
  sv: {
    // Navigation
    'nav.about': 'Om oss',
    'nav.services': 'Tjänster',
    'nav.pricing': 'Priser',
    'nav.faq': 'Vanliga frågor',
    'nav.blog': 'Blogg',
    'nav.contact': 'Kontakt',
    'nav.book': 'Boka konsultation',
    
    // Home
    'home.hero.title': 'Expert dermatologisk vård. Personliga lösningar.',
    'home.hero.subtitle': 'Omfattande dermatologiska tjänster för hudcancerscreening, aknebehandling, laserterapi och alla dina hudproblem. Jag kombinerar expertis från Barcelonas ledande medicinska institution med års internationell erfarenhet inom evidensbaserad dermatologi.',
    'home.hero.cta1': 'Boka konsultation',
    'home.hero.cta2': 'Läs om tjänster',
    'home.about.title': 'Möt Dr. Raquel Molina',
    'home.about.bio1': 'Jag är dermatolog utbildad vid Universitat Autònoma de Barcelona (UAB), en av Spaniens mest prestigefyllda medicinska institutioner. Min passion för dermatologi kommer från förståelsen att hudhälsa djupt påverkar det allmänna välbefinnandet och självförtroendet.',
    'home.about.bio2': 'Efter min specialisering tillbringade jag formativa år i Sverige, där jag lärde mig evidensbaserad medicin och patientcentrerade vårdmetoder som fortsätter att vägleda min praktik idag. Jag förblir engagerad i att ligga i framkant av dermatologisk forskning genom internationella konferenser och kontinuerligt lärande.',
    'home.about.bio3': 'Jag specialiserar mig på omfattande dermatologi—från hudcancerscreening och aknebehandling till laserterapi och håravfallslösningar. Jag har sett på nära håll hur rätt dermatologisk vård återställer inte bara hudhälsan, utan även självförtroendet och livskvaliteten.',
    'home.about.bio_link': 'Läs fullständig biografi',
    'home.services.title': 'Omfattande dermatologiska tjänster',
    'home.services.subtitle': 'Evidensbaserade behandlingar anpassade efter dina specifika behov',
    'home.faq.title': 'Vanliga frågor',
    'home.faq.subtitle': 'Få svar på vanliga frågor om hudcancer, akne, laserbehandlingar och alla dermatologiska frågor',
    'home.faq.view': 'Visa alla frågor',
    'home.cta.title': 'Redo att ta första steget?',
    'home.cta.subtitle': 'Boka en konsultation för att diskutera dina dermatologiska frågor och utforska personliga behandlingsalternativ.',
    'home.cta.button': 'Boka din konsultation',
    'home.services.skin_cancer': 'Hudcancerscreening',
    'home.services.skin_cancer_desc': 'Professionell utvärdering av födelsemärken, hudcancerdetektering och förebyggande strategier. Tidig upptäckt räddar liv.',
    'home.services.acne': 'Aknebehandling',
    'home.services.acne_desc': 'Personliga aknelösningar från mild till svår. Avancerade behandlingar anpassade efter din hudtyp och behov.',
    'home.services.laser': 'Laser och avancerade terapier',
    'home.services.laser_desc': 'Banbrytande laserbehandlingar för håravfall, hudföryngring och olika dermatologiska tillstånd.',
    'home.services.view_all': 'Visa alla tjänster',
    'home.why_choose.title': 'Varför välja Dr. Raquel Molina?',
    'home.why_choose.subtitle': 'Internationell expertis kombinerad med personlig, evidensbaserad vård',
    'home.why_choose.education': 'Utbildning i Barcelona',
    'home.why_choose.education_desc': 'Utbildad vid Spaniens ledande medicinska institution',
    'home.why_choose.international': 'Internationell erfarenhet',
    'home.why_choose.international_desc': 'Års praktik i Sverige och utomlands',
    'home.why_choose.evidence': 'Evidensbaserad',
    'home.why_choose.evidence_desc': 'Senaste dermatologisk forskning och teknik',
    'home.why_choose.response': 'Snabbt svar',
    'home.why_choose.response_desc': '2 timmars svar, 30 min vid akut',
    'home.urgent.title': 'Behöver du akut hjälp?',
    'home.urgent.subtitle': 'Berätta om ditt hudproblem så svarar vi inom 2 timmar. Vid akuta fall garanterar vi svar inom 30 minuter.',
    'home.urgent.button': 'Skicka din fråga nu',
    'home.urgent.guarantee': 'Vår svarsgaranti:',
    'home.urgent.hours2': '2 timmar:',
    'home.urgent.hours2_desc': 'Standard svarstid',
    'home.urgent.hours30': '30 minuter:',
    'home.urgent.hours30_desc': 'Akuta fall',
    'home.urgent.hours24': '24-48 timmar:',
    'home.urgent.hours24_desc': 'Tillgänglighet för tidsbokning',
    
    // Services
    'services.title': 'Tjänster',
    'services.subtitle': 'Omfattande dermatologiska tjänster specialiserade på håravfallsbehandling',
    'services.hair_loss.title': 'Håravfallsbehandling',
    'services.hair_loss.desc': 'Omfattande utvärdering och personliga behandlingsplaner för alla typer av håravfall, inklusive androgenetisk alopeci, telogent effluvium och alopecia areata.',
    'services.laser.title': 'Lågintensiv laserterapi',
    'services.laser.desc': 'Avancerad laserteknik för att stimulera hårfolliklar och främja återväxt. FDA-godkänd med minimala biverkningar.',
    'services.general.title': 'Allmän dermatologi',
    'services.general.desc': 'Komplett dermatologisk vård inklusive borttagning av födelsemärken, hudcancerscreening, aknebehandling och allmän hudhälsa.',
    'services.aquapure.title': 'AquaPure fuktgivande ansiktsbehandling',
    'services.aquapure.desc': 'Avancerad vattenbaserad ansiktsbehandling som kombinerar djuprengöring, elektroporering och kollagenstimulering för strålande, föryngrad hud.',
    'services.consultation.title': 'Din första konsultation',
    'services.consultation.step1': 'Inledande bedömning',
    'services.consultation.step1_desc': 'Omfattande utvärdering av ditt tillstånd, sjukdomshistoria och behandlingsmål.',
    'services.consultation.step2': 'Diagnos',
    'services.consultation.step2_desc': 'Professionell diagnos med klinisk undersökning och avancerade diagnostiska verktyg vid behov.',
    'services.consultation.step3': 'Behandlingsplan',
    'services.consultation.step3_desc': 'Personlig behandlingsplan anpassad efter ditt specifika tillstånd och mål.',
    'services.consultation.step4': 'Uppföljning',
    'services.consultation.step4_desc': 'Regelbunden övervakning och justeringar för att säkerställa optimala resultat.',
    
    // FAQ
    'faq.title': 'Vanliga frågor',
    'faq.subtitle': 'Svar på vanliga frågor om håravfallsbehandling och dermatologi från Dr. Raquel Molina',
    
    // Blog
    'blog.title': 'Blogg',
    'blog.subtitle': 'Insikter och artiklar från Dr. Raquel Molina om håravfallsbehandling, dermatologi och hudvård. Baserat på klinisk erfarenhet och den senaste evidensbaserade forskningen.',
    'blog.search': 'Sök artiklar...',
    'blog.showing': 'Visar',
    'blog.of': 'av',
    'blog.articles': 'artiklar',
    'blog.no_results': 'Inga artiklar hittades som matchar din sökning.',
    'blog.questions': 'Har du frågor?',
    'blog.questions_subtitle': 'Kolla in min FAQ-sektion eller boka en konsultation för att diskutera dina specifika dermatologiska frågor.',
    'blog.view_faq': 'Visa vanliga frågor',
    'blog.book': 'Boka konsultation',
    'blog.related': 'Relaterade artiklar',
    'blog.read_more': 'Läs mer',
    
    // Contact
    'contact.title': 'Kontakt och bokning',
    'contact.subtitle': 'Kontakta oss för att boka din dermatologiska konsultation',
    'contact.form.name': 'Fullständigt namn',
    'contact.form.email': 'E-postadress',
    'contact.form.phone': 'Telefonnummer',
    'contact.form.concern': 'Huvudsakligt problem',
    'contact.form.message': 'Meddelande',
    'contact.form.submit': 'Skicka meddelande',
    'contact.info.title': 'Kontaktinformation',
    'contact.info.email': 'skinbyraq@gmail.com',
    'contact.info.phone': '+34 627 18 32 20',
    'contact.info.location': 'Barcelona, Spanien',
    
    // About
    'about.title': 'Om Dr. Raquel Molina',
    'about.bio': 'Dr. Raquel Molina är en legitimerad dermatolog med omfattande erfarenhet av håravfallsbehandling och allmän dermatologi. Utbildad vid Universitat Autònoma de Barcelona, en av Spaniens mest prestigefyllda medicinska institutioner, kombinerar Dr. Molina rigorös vetenskaplig utbildning med medkännande patientvård.',
    'about.credentials': 'Meriter och erfarenhet',
    'about.education': 'Medicinsk utbildning',
    'about.education_detail': 'Universitat Autònoma de Barcelona (UAB) - Läkarexamen och specialisering i dermatologi',
    'about.international': 'Internationell erfarenhet',
    'about.international_detail': 'Flera års arbete i Sverige med expertis inom evidensbaserad dermatologi och patientcentrerade vårdmetoder',
    'about.conferences': 'Professionell utveckling',
    'about.conferences_detail': 'Regelbundet deltagande och presentationer vid internationella dermatologikonferenser',
    'about.philosophy': 'Behandlingsfilosofi',
    'about.philosophy_detail': 'Dr. Molina tror på omfattande, personlig vård. Hon tar sig tid att förstå varje patients unika situation och utvecklar skräddarsydda behandlingsplaner som kombinerar de senaste evidensbaserade terapierna med beprövade traditionella metoder.',
    
    // Footer
    'footer.about': 'Professionella dermatologiska tjänster specialiserade på håravfallsbehandling och omfattande hudvård.',
    'footer.services': 'Tjänster',
    'footer.hair_loss': 'Håravfallsbehandling',
    'footer.laser': 'Laserterapi',
    'footer.general': 'Allmän dermatologi',
    'footer.resources': 'Resurser',
    'footer.faq': 'Vanliga frågor',
    'footer.blog': 'Blogg',
    'footer.about_dr': 'Om Dr. Raquel Molina',
    'footer.contact': 'Kontakt',
    'footer.copyright': '© 2026 Skin by Raquel. Alla rättigheter förbehållna.',
    
    // Testimonials
    'testimonials.title': 'Patientomdömen',
    'testimonials.subtitle': 'Verkliga berättelser från patienter som har upplevt Dr. Raquel Molinas vård',
    
    // Booking
    'booking.title': 'Boka din konsultation',
    'booking.subtitle': 'Boka din dermatologiska tid hos Dr. Raquel Molina',
    
    // Videos
    'videos.title': 'Utbildningsvideor',
    'videos.subtitle': 'Lär dig om dermatologiska behandlingar och hudvård från Dr. Raquel Molina',
  }
};

export function LanguageProvider({ children, initialLanguage = 'es' }: { children: React.ReactNode; initialLanguage?: Language }) {
  const [language, setLanguage] = useState<Language>(initialLanguage);

  // Sync language with URL-based initialLanguage whenever it changes
  useEffect(() => {
    setLanguage(initialLanguage);
  }, [initialLanguage]);

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations[Language]] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}
